import { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import api from "@/lib/api";
import {
  getQueue, addToQueue, removeFromQueue, clearQueue,
  remapOfflineReportId, removeOfflineReport, getOfflineReports,
  isOfflineId,
} from "@/lib/offlineStorage";

const OfflineContext = createContext(null);

export function OfflineProvider({ children }) {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [queueLength, setQueueLength] = useState(0);
  const [syncing, setSyncing] = useState(false);
  const [lastSyncResult, setLastSyncResult] = useState(null);
  const syncRef = useRef(false);

  // Monitor connectivity
  useEffect(() => {
    const goOnline = () => setIsOnline(true);
    const goOffline = () => setIsOnline(false);
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);

    // Periodic connectivity check every 15s
    const interval = setInterval(async () => {
      try {
        const res = await fetch(process.env.REACT_APP_BACKEND_URL + "/api/health", {
          method: "GET",
          cache: "no-store",
          signal: AbortSignal.timeout(5000),
        });
        setIsOnline(res.ok);
      } catch {
        setIsOnline(false);
      }
    }, 15000);

    return () => {
      window.removeEventListener("online", goOnline);
      window.removeEventListener("offline", goOffline);
      clearInterval(interval);
    };
  }, []);

  // Load queue length on mount
  useEffect(() => {
    getQueue().then((q) => setQueueLength(q.length));
  }, []);

  // Auto-sync when coming back online
  useEffect(() => {
    if (isOnline && queueLength > 0 && !syncing) {
      syncQueue();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOnline]);

  const enqueue = useCallback(async (item) => {
    const queue = await addToQueue(item);
    setQueueLength(queue.length);
    return queue;
  }, []);

  const syncQueue = useCallback(async () => {
    if (syncRef.current) return;
    syncRef.current = true;
    setSyncing(true);
    let synced = 0;
    let failed = 0;

    try {
      const queue = await getQueue();
      if (!queue.length) {
        setSyncing(false);
        syncRef.current = false;
        return { synced: 0, failed: 0 };
      }

      // Sort by queued_at to maintain order
      queue.sort((a, b) => a.queued_at - b.queued_at);

      // Phase 1: Sync offline report creations first
      const reportCreations = queue.filter((q) => q.type === "create_report");
      for (const item of reportCreations) {
        try {
          const res = await api.post("/reports", item.payload);
          const realId = res.report?.id;
          if (realId && isOfflineId(item.reportId)) {
            await remapOfflineReportId(item.reportId, realId);
            await removeOfflineReport(item.reportId);
          }
          await removeFromQueue(item.id);
          synced++;
        } catch (err) {
          console.error("Sync create_report failed:", err);
          failed++;
        }
      }

      // Phase 2: Sync shift logs and other events
      const refreshedQueue = await getQueue();
      const events = refreshedQueue.filter((q) => q.type !== "create_report");
      for (const item of events) {
        try {
          if (item.type === "create_shift_log") {
            await api.post(`/reports/${item.reportId}/shift-logs`, item.payload);
          } else if (item.type === "delete_shift_log") {
            await api.delete(`/reports/${item.reportId}/shift-logs/${item.logId}`);
          }
          await removeFromQueue(item.id);
          synced++;
        } catch (err) {
          console.error(`Sync ${item.type} failed:`, err);
          failed++;
        }
      }
    } catch (err) {
      console.error("Sync error:", err);
    } finally {
      const remaining = await getQueue();
      setQueueLength(remaining.length);
      setSyncing(false);
      syncRef.current = false;
      const result = { synced, failed };
      setLastSyncResult(result);
      return result;
    }
  }, []);

  const refreshQueueLength = useCallback(async () => {
    const q = await getQueue();
    setQueueLength(q.length);
  }, []);

  return (
    <OfflineContext.Provider
      value={{
        isOnline,
        queueLength,
        syncing,
        lastSyncResult,
        enqueue,
        syncQueue,
        refreshQueueLength,
      }}
    >
      {children}
    </OfflineContext.Provider>
  );
}

export function useOffline() {
  const ctx = useContext(OfflineContext);
  if (!ctx) throw new Error("useOffline must be used within OfflineProvider");
  return ctx;
}
